import { gql } from '@apollo/client';
import { BILLING_FIELDS } from '@/graphql/queries';

export const CREATE_ORGANISATION = gql`
  mutation createOrganisation($input: CreateOrganisationInput) {
    createOrganisation(input: $input) {
      status
      name
      defaultRoute
      defaultRouteType
      advancedRouting {
        send {
          followUpAction
          followUpRoute
        }
        receive {
          followUpAction
          followUpRoute
        }
      }
      legalEntity {
        taxId
        taxIdType
        address {
          city
          country
          line1
          line2
          postalCode
          state
        }
      }
      currency
      id
      parentId
      country
      notificationEmails
      access {
        scopes
        sourceIps
        tokenIds
      }
      plan
    }
  }
`;

export const UPDATE_ORGANISATION = gql`
  mutation updateOrganisation($params: UpdateOrganisationInput, $orgId: String!) {
    updateOrganisation(params: $params, orgId: $orgId) {
      status
      name
      defaultRoute
      defaultRouteType
      advancedRouting {
        send {
          followUpAction
          followUpRoute
        }
        receive {
          followUpAction
          followUpRoute
        }
      }
      legalEntity {
        taxId
        taxIdType
        address {
          city
          country
          state
          postalCode
          line2
          line1
        }
        taxRepresentative {
          name
          country
          taxId
          address {
            city
            country
            state
            postalCode
            line1
          }
        }
      }
      currency
      id
      parentId
      peppolId
      country
      notificationEmails
      access {
        scopes
        sourceIps
        tokenIds
      }
      plan
      ${BILLING_FIELDS}
      birchstreet {
        subscriberId
      }
      are {
        contact {
          name
          phone
          email
        }
        legalRegistration {
          agencyId
          agencyName
        }
      }
      chl {
        taxIdType
      }
      den {
        schemeIds {
          client
          legalEntity
          company
        }
        contact {
          email
        }
        endPointId
        vatNumber
      }
      deu {
        contact {
          name
          email
          phone
        }
        taxRepresentative {
          name
          taxId
          country
        }
      }
      egy {
        onboarded
        sftp {
          host
          username
          password
          port
        }
      }
      emailSettings {
        brand {
          color
        }
        replyTo
        cc
        bcc
        sendToRecipient
        sendToSender
        emailNotes
      }
      fra {
        onboarded
        profileType
        reportingFrequency
        vatScheme
        vatNumber
      }
      grc {
        auth {
          key
        }
        onboarded
        series
      }
      hun {
        contact {
          name
          phone
          email
        }
        vatCode
        bankAccount {
          number
        }
        vatGroup {
          taxId
          vatCode
          country
        }
        auth {
          username
          password
          xmlExchangeKey
          xmlSignatureKey
        }
      }
      hrv {
        onboarded
        auth {
          apiKey
        }
        contact {
          name
          email
          phone
        }
        electronicAddress {
          schemeId
          endpointId
          authority
        }
        oib
      }
      idn {
        auth {
          apiKey
        }
        taxRepresentative {
          title
          name
          taxId
          passphrase
          address {
            city
          }
        }
      }
      ind
      ita {
        contact {
          email
          phone
        }
        issuer
        issuingSystem
        taxIdType
        taxRegime
      }
      jor {
        onboarded
        auth {
          orgKey
          apiKey
          channel
        }
      }
      jpn {
        electronicAddress {
          schemeId
          endpointId
        }
      }
      ken {
        onboarded
        auth {
          apiKey
        }
      }
      kor {
        auth {
          linkId
          userId
          corpNum
          secretKey
        }
        businessRegistrationNum
        taxRegID
        representativeName
      }
      mex {
        taxIdType
        detecno {
          csdPrivateKeyPem
          csdCertificateBase64
          csdCertificateNumber
        }
      }
      mock {
        email
      }
      mus {
        auth {
          username
          password
          ebsMraId
        }
        tan
        brn
        ebsCounterNo
        cashierId
        businessPhoneNo
      }
      mys {
        onboarded
        clientId
        clientSecret
        clientIdFallback
        clientSecretFallback
        sstRegistrationNumber
        tourismTaxRef
        industryClassificationCode
        businessActivity
        brnNumber
        contact {
          phone
        }
      }
      nemHandel {
        id
      }
      nga {
        onboarded
        auth {
          apiKey
        }
        contact {
          phone
          email
        }
      }
      pak {
        auth {
          userName
          password
          apiKey
          webDnaCode
        }
      }
      pol {
        auth {
          x509Certificate
          privateKey
          offlineCertificate
          offlinePrivateKey
          ksefToken
        }
        contact {
          phone
          email
        }
      }
      ppl {
        electronicAddress {
          schemeId
          endpointId
        }
        receiveOnPeppol
      }
      prt {
        onboarded
        invoiceSeriesName
        simplifiedInvoiceSeriesName
        creditNoteSeriesName
      }
      rou
      sapConcur {
        onboarded
        scope
        tokenType
        refreshToken
        refreshExpiresIn
        geolocation
        policyId
        ledgerCode
        customInfo
      }
      stripe {
        accountId
        linkedAt
        linkedByEmail
      }
      sau {
        onboarded
        certs {
          crt
          privateKey
          compliance {
            requestId
            token
            tokenType
            secret
          }
          production {
            requestId
            token
            tokenType
            secret
          }
        }
      }
      pak {
        auth {
          userName
          password
          apiKey
          webDnaCode
        }
      }
      tur {
        onboarded
        auth {
          username
          password
        }
        taxOffice
      }
      twn {
        contact {
          name
          phone
          email
          fax
        }
        username
        password
        companyKey
      }
      yokoy {
        onboarded
        organisationRef
        legalEntityRef
        customInfo
        clientId
        clientSecret
      }
    }
  }
`;

export const SEND_INVITE = gql`
  mutation sendInvite($email: String!, $orgId: String!, $group: String) {
    sendInvite(email: $email, orgId: $orgId, group: $group) {
      success
      message
    }
  }
`;

export const UPDATE_SCOPES = gql`
  mutation updateUserScopes($orgId: String, $userId: String, $scopes: [String], $groups: [String]) {
    updateUserScopes(orgId: $orgId, userId: $userId, scopes: $scopes, groups: $groups) {
      success
      message
    }
  }
`;

export const GENERATE_AUTHTOKEN = gql`
  mutation generateToken($orgId: String!, $name: String, $expiresAt: AWSDateTime) {
    generateJwtToken(orgId: $orgId, requestType: generate, name: $name, expiresAt: $expiresAt) {
      token
      tokenId
      name
      maskedValue
      createdAt
      createdBy
      expiresAt
    }
  }
`;

export const REVOKE_AUTHTOKEN = gql`
  mutation revokeToken($orgId: String!, $tokenId: String!) {
    revokeJwtToken(orgId: $orgId, requestType: revoke, tokenId: $tokenId) {
      country
      currency
      id
      access {
        scopes
        sourceIps
        tokenIds
      }
      legalEntity {
        address {
          city
          country
          line1
          line2
          postalCode
          state
        }
        taxId
        taxIdType
      }
      name
      status
    }
  }
`;
export const CREATE_INVOICE = gql`
  mutation createInvoice($params: AWSJSON!, $orgId: String!, $userId: String) {
    createInvoice(params: $params, orgId: $orgId, userId: $userId) {
      id
      created
      issued
      modified
      orgId
      parentOrgId
      isSelfBilled
      recipient {
        address {
          city
          country
          line1
          line2
          postalCode
          state
        }
        contact {
          email
          phone
        }
        customInfo
        name
        peppolId {
          endpointId
        }
        taxId
        taxScheme
        taxRepresentative {
          name
          taxId
          address {
            city
            country
            line1
            line2
            postalCode
            state
          }
        }
      }
      sender {
        address {
          city
          country
          line1
          postalCode
          state
        }
        name
        taxId
        taxRepresentative {
          name
          taxId
          address {
            city
            country
            line1
            line2
            postalCode
            state
          }
        }
      }
      ref
      route
      status
      type
      total {
        amount
        currency
      }
      lines
      customInfo {
        deu
        chn
        fra
        grc
        hun
        ita
        ind
        taIND {
          SignedQRCode
        }
        mex {
          docUrl
          reasonCode
          typeCode
          recipientTaxIdType
          recipientIndustryClassificationCode
        }
        mys
        pak
        prt
        rou
        sau
        tur
        twn
      }
      attachments {
        name
        hash
        contentType
        contentLength
        url
      }
      notes
      error {
        description
        name
        message
      }
    }
  }
`;

export const CREATE_CREDITNOTE = gql`
  mutation createCreditNote($params: AWSJSON!, $orgId: String!, $userId: String) {
    createCreditNote(params: $params, orgId: $orgId, userId: $userId) {
      id
      created
      invoiceRefs
      issued
      modified
      isSelfBilled
      customInfo {
        chn
        deu
        fra
        grc
        hun
        ita
        ind
        taIND {
          SignedQRCode
        }
        mex {
          docUrl
          reasonCode
          typeCode
          recipientTaxIdType
          recipientIndustryClassificationCode
        }
        mys
        pak
        prt
        rou
        sau
        tur
        twn
      }
      lines
      total {
        amount
        currency
      }
      status
      ref
      route
      recipient {
        name
        taxId
        taxScheme
        customInfo
        peppolId {
          endpointId
        }
        address {
          city
          country
          line1
          line2
          postalCode
          state
        }
        contact {
          email
          phone
        }
        taxRepresentative {
          name
          taxId
          address {
            country
          }
        }
      }
      sender {
        name
        taxId
        address {
          city
          country
          line1
          line2
          postalCode
          state
        }
        taxRepresentative {
          name
          taxId
          address {
            country
          }
        }
      }
      orgId
      parentOrgId
      attachments {
        name
        hash
        contentType
        contentLength
        url
      }
      notes
      error {
        description
        name
        message
      }
    }
  }
`;

export const REMOVE_ORG_USER = gql`
  mutation removeOrgUser($orgId: String!, $userId: String!) {
    removeOrgUser(orgId: $orgId, userId: $userId) {
      success
      message
    }
  }
`;

export const UPDATE_INVOICE_STATUS = gql`
  mutation UpdateInvoiceStatus($orgId: String!, $invoiceId: String!, $statusUpdate: AWSJSON!) {
    updateInvoiceStatus(orgId: $orgId, invoiceId: $invoiceId, statusUpdate: $statusUpdate) {
      status
      id
      created
    }
  }
`;

export const UPDATE_CREDITNOTE_STATUS = gql`
  mutation UpdateCreditNoteStatus($orgId: String!, $creditNoteId: String!, $statusUpdate: AWSJSON!) {
    updateCreditNoteStatus(orgId: $orgId, creditNoteId: $creditNoteId, statusUpdate: $statusUpdate) {
      status
      id
      created
    }
  }
`;

export const ARE_ASP_BRIDGE = gql`
  mutation areAspBridge($input: AreBridgeInput!) {
    areAspBridge(input: $input) {
      ok
      action
      status
      entity {
        tin
        legalType
        entityNameEn
        entityNameAr
        vatTrn
        effectiveDate
      }
      errorCode
      errorMessage
    }
  }
`;

export const CREATE_BILLING_CHECKOUT_SESSION = gql`
  mutation createBillingCheckoutSession($orgId: String!, $tier: String!) {
    createBillingCheckoutSession(orgId: $orgId, tier: $tier) {
      url
    }
  }
`;

export const CREATE_BILLING_PORTAL_SESSION = gql`
  mutation createBillingPortalSession($orgId: String!) {
    createBillingPortalSession(orgId: $orgId) {
      url
    }
  }
`;

// Stripe e-Invoicing app link — written only through these two mutations (never via
// updateOrganisation). Unrelated to the subscription billing mutations above.
export const LINK_STRIPE_ACCOUNT = gql`
  mutation linkStripeAccount($orgId: String!, $accountId: String!, $token: String!) {
    linkStripeAccount(orgId: $orgId, accountId: $accountId, token: $token) {
      accountId
      linkedAt
    }
  }
`;

export const UNLINK_STRIPE_ACCOUNT = gql`
  mutation unlinkStripeAccount($orgId: String!) {
    unlinkStripeAccount(orgId: $orgId) {
      accountId
    }
  }
`;
